import "@tento-ui/components/round";
import "@tento-ui/components/event-details/event-details-content";
import "@tento-ui/components/core/avatar-editable";
import "@tento-ui/components/disclaimer";

import { html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { AvatarEditable } from "@tento-ui/components/core/avatar-editable";
import { Button } from "@tento-ui/components/button/button";
import { Constants } from "@tento-core/constants";
import { Disclaimer } from "@tento-ui/components/disclaimer";
import {
  DateTime,
  DateTimeRegion,
  type MsSinceEpoch,
  TimezoneRegion,
  type Tzname,
  Utc,
} from "@tento-chrono";
import { Divider } from "@tento-ui/components/divider";
import { EventDetailsContent } from "@tento-ui/components/event-details/event-details-content";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { Round } from "@tento-ui/components/round";
import { Toast } from "@tento-ui/components/toast/toast";
import { Tracing } from "@tento-core/tracing";

/**
 * The confirmed-booking receipt — the success surface shown after a booking is
 * made. Mirrors the `BookingConfirmation` booking-form modal: the public
 * booking route opens it over the picker when the base route sees
 * `?confirmed={bookingId}` (so the receipt survives a hard refresh — the
 * booking UUID is the unguessable capability the server reads back via
 * `SchedulingLinksClient.getBooking`). The booked meeting is rendered as the
 * real `EventDetailsContent` card on its `std-round` background.
 */
@component({
  name: "lona-booking-receipt",
})
export class BookingReceipt extends LonaModalWebComponent {
  private $bg: Round = this.hydrate<Round>("bg").bind(Round.Props.MODAL);
  private receiptUrl: string = "";
  private resolve: Option<() => void> = null;

  constructor() {
    super();
    GESTURE_MANAGER.addPointerEvent(this.$("copy-link"), {
      onClick: () => this.copyLink(),
    });
    GESTURE_MANAGER.addPointerEvent(this.$("done-button"), {
      onClick: () => this.close(),
    });
  }

  static async show(props: BookingReceipt.Props): Promise<void> {
    return new Promise((resolve) => {
      const $modal = BookingReceipt.make();
      $modal.bind(props, resolve);
      $modal.reveal();
    });
  }

  /** Build a bound receipt card for inline embedding (storybook / previews). */
  static makeWith(props: BookingReceipt.Props): BookingReceipt {
    const $receipt = BookingReceipt.make();
    $receipt.bind(props, () => {});
    return $receipt;
  }

  bind(props: BookingReceipt.Props, resolve: () => void): this {
    this.resolve = resolve;
    this.receiptUrl = props.receiptUrl ?? "";
    void this.bindEventDetails(props);
    return this;
  }

  // The event-details time-label needs a real `TimezoneRegion` (loaded async from
  // the static host), so the booked-event card fills in a tick after `bind`.
  private async bindEventDetails(props: BookingReceipt.Props): Promise<void> {
    const start = DateTime.fromMse(props.startTimeMs as MsSinceEpoch, Utc);
    const range = new DateTime.Range(
      start,
      start.add({ mins: props.durationMinutes }),
    );
    const tz = await TimezoneRegion.get(props.timezone as Tzname);
    const time = DateTimeRegion.Range.fromSingleTz(range, tz);

    const $details = EventDetailsContent.make().bind({
      title: props.linkTitle,
      time,
      onUpdateTime: Constants.EMPTY_FUNCTION,
      editable: false,
      modalContext: "booking-receipt",
    });
    $details.assignStyles({ width: "100%" });

    const $icon = AvatarEditable.make();
    $icon.bind({
      content: "📅",
      contentBg: "var(--gray-dimmed)",
      badgeBg: "var(--gray)",
      badgeContent: null,
      disabled: true,
    });
    $icon.setAttribute("slot", "icon");
    $icon.assignStyles({ marginRight: "12px" });
    $details.appendChild($icon);

    const $fields = EventDetailsContent.FieldContainer.make();
    $fields
      .bind(
        {
          host: EventDetailsContent.Field.makeText(
            `${props.durationMinutes} min with ${props.hostName}`,
          ),
        },
        ["host"],
      )
      .bindVisible(new Set(["host"]));
    $details.appendChild($fields);

    const $round = Round.makeWith(Round.Props.MODAL).assignStyles({
      padding: "16px 12px",
    });
    $round.appendChild($details);

    // The invite confirmation is a meta note, not an event detail — render it as
    // a disclaimer above the event card.
    const $disclaimer = Disclaimer.makeWith(
      `A calendar invite was sent to ${props.bookerEmail}.`,
    );
    this.$("event").replaceChildren($disclaimer, $round);
  }

  private copyLink(): void {
    const url = this.receiptUrl || globalThis.location?.href || "";
    if (!url) return;
    void navigator.clipboard
      .writeText(url)
      .then(() => Toast.info("Receipt link copied"))
      .catch(() => Toast.info("Couldn't copy the link"));
    Tracing.log("public-booking-receipt-copy", { url });
  }

  private close(): void {
    this.resolve?.();
    this.resolve = null;
    this.dismiss();
  }

  onModalDismiss(): void {
    this.resolve?.();
    this.resolve = null;
  }

  static $styles: Style.Compat[] = [
    LonaModalWebComponent.$style,
    LonaModalHeader.$style,
    Divider.$hr,
    Button.$style,
    css`
      :host {
        --overlay-border-radius: 8px;
        --std-modal-max-width: 420px;
      }

      #root {
        position: relative;
      }

      hr {
        margin-block: 0;
      }

      #event {
        gap: 10px;
        padding: 16px 20px 0;
      }

      #actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 16px 20px 20px;
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-round id="bg" floating></std-round>
      <std-col class="lona-modal-header">
        <h1>Booking confirmed</h1>
      </std-col>
      <hr />
      <std-col id="event"></std-col>
      <std-row id="actions">
        <button id="copy-link">Copy receipt link</button>
        <button id="done-button" primary>Done</button>
      </std-row>
    </std-col>
  `;
}

export namespace BookingReceipt {
  export interface Props {
    hostName: string;
    linkTitle: string;
    durationMinutes: number;
    startTimeMs: number;
    timezone: string;
    bookerName: string;
    bookerEmail: string;
    receiptUrl?: Option<string>;
  }
}
